import {DragDropContext, Draggable, DropResult, Droppable} from '@hello-pangea/dnd'
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  GraduationCap,
  GripVertical,
  Info,
  Layers,
  LayoutGrid,
  Pencil,
  Plus,
  School,
  Trash2,
  Users,
} from 'lucide-react'
import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'

import {loadCurriculumDefaults, usesPlainCurriculumNames} from '../curriculumPrefill'
import {OnboardingPartialSubmit, WizardData} from '../index'
import {StoredSchoolSystemDetails, readStoredSchoolSystemDetails} from '../schoolSystemDetailsCache'
import {
  CurriculumStructure,
  LevelDraft,
  applyAcademicStructure,
  buildAcademicStructure,
  classNamePrefixFor,
  defaultSectionsFor,
  duplicateLevelNames,
  isStructureComplete,
  levelIssues,
  subjectPoolForCurriculum,
} from '../utils/academicStructure'
import {curriculumPrefixes, stripCurriculumPrefix} from '../utils/curriculumDisplay'
import {FALLBACK_CURRICULUM} from '../utils/mapOnboardingData'
import Step3LevelDrawer from './Step3LevelDrawer'
import WizardConfirmDialog from './WizardConfirmDialog'

/** Subject chips shown inline on a level row before collapsing into "+N more". */
const MAX_INLINE_SUBJECTS = 5
/** Subject chips per line in the Subjects column. */
const SUBJECTS_PER_LINE = 3

const chunk = <T,>(items: T[], size: number): T[][] =>
  Array.from({length: Math.ceil(items.length / size)}, (_, i) =>
    items.slice(i * size, i * size + size)
  )

const LEVEL_ICONS = [Users, LayoutGrid, BookOpen, GraduationCap, Layers, School] as const

interface Props {
  data: WizardData
  updateData: (data: Partial<WizardData>, options?: {prefill?: boolean}) => void
  isDark: boolean
  /** True until the resume fetches settle — nothing is seeded from defaults before then. */
  isHydrating?: boolean
  /** Reports the Academic Structure position for URL syncing (always null — see the mount effect). */
  onActiveSubStepChange?: (subStepSlug: string | null) => void
  onNext?: () => void | Promise<void>
  onBack?: () => void
  onPartialSubmit?: OnboardingPartialSubmit
  isSubmitting?: boolean
  onAutofillLoadingChange?: (isLoading: boolean) => void
}

type DrawerState =
  | {mode: 'add'; curriculum: string}
  | {mode: 'edit'; curriculum: string; levelId: string}

/**
 * Step 3 — "Academic Structure" as a single review page: one card per curriculum picked on
 * Setup Basics, pre-filled with that curriculum's levels, classes, sections and subjects. Levels
 * can be reordered (drag handle), removed, edited or added; Edit/Add open a right-side drawer
 * over the page (Step3LevelDrawer). Every change is folded straight back into `manualData`
 * through `applyAcademicStructure`, so the later steps and the bulk-import see one consistent
 * set of classes/subjects.
 */
const Step3AcademicStructure: FC<Props> = ({
  data,
  updateData,
  isDark,
  isHydrating = false,
  onActiveSubStepChange,
  onNext,
  onBack,
  onPartialSubmit,
  isSubmitting = false,
  onAutofillLoadingChange,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const t = (suffix: string, values?: Record<string, any>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.STEP3_REVIEW.${suffix}` as any}, values)

  const md = data.manualData
  const curriculums = useMemo(
    () => Array.from(new Set((md?.curriculums ?? []).map((c) => c.trim()).filter(Boolean))),
    [md?.curriculums]
  )

  // The page is the whole step — the resume pointer and URL sit on the BARE step
  // (`/onboarding/academic-structure`, sub-step null), exactly like Step 2. The pointer's
  // analytics name then falls back to the step's first position, `curriculum_curriculum`,
  // which the Setup Basics batch already completed, so landing here records nothing new.
  // Any real sub-step would be wrong: node-api's ordinal backfill credits every position
  // BEFORE the pointer as completed (a pointer on `review` reported levels/classes/sections
  // as done the moment the page rendered), and even `levels` would record a "reached" row
  // on render. All five curriculum_* positions are recorded only by the Next batch
  // (handleStep3Completion).
  useEffect(() => {
    onActiveSubStepChange?.(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // No curriculum picked on Setup Basics (legacy tenants): use the same fallback bucket the old
  // Levels screen seeded so the user can still define levels by hand.
  useEffect(() => {
    if (isHydrating || curriculums.length > 0) return
    updateData({manualData: {...md, curriculums: [FALLBACK_CURRICULUM]}}, {prefill: true})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrating, curriculums.length])

  // --- Curriculum defaults (timetable/prefill, legacy school-system details as fallback) ---
  const [details, setDetails] = useState<StoredSchoolSystemDetails>(() =>
    readStoredSchoolSystemDetails()
  )
  const [isFetchingDetails, setIsFetchingDetails] = useState(false)
  const attemptedRef = useRef<Set<string>>(new Set())

  // Hydration may switch the level naming (plain vs prefixed names), which empties the cache
  // read above; re-read it so the defaults are refetched in the right form.
  useEffect(() => {
    if (!isHydrating) setDetails(readStoredSchoolSystemDetails())
  }, [isHydrating])

  useEffect(() => {
    if (isHydrating || isFetchingDetails) return
    const missing = curriculums.filter(
      (name) => name !== FALLBACK_CURRICULUM && !details[name] && !attemptedRef.current.has(name)
    )
    if (missing.length === 0) return
    missing.forEach((name) => attemptedRef.current.add(name))
    setIsFetchingDetails(true)
    onAutofillLoadingChange?.(true)
    const finish = () => {
      setIsFetchingDetails(false)
      onAutofillLoadingChange?.(false)
    }
    // Curricula picked from the country catalog resolve through timetable/prefill (levels →
    // grades → subjects), older picks through the legacy details endpoint; the loader merges
    // both into the shared cache this page reads.
    loadCurriculumDefaults(
      dispatch,
      {country: data.country, curriculums: missing, codes: md?.curriculumCodes},
      () => {
        setDetails(readStoredSchoolSystemDetails())
        finish()
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curriculums, details, isHydrating, isFetchingDetails])

  const structures = useMemo(() => buildAcademicStructure(md, details), [md, details])
  const defaultSections = useMemo(() => defaultSectionsFor(md), [md])

  const commit = (next: CurriculumStructure[], options?: {prefill?: boolean}) =>
    updateData({manualData: {...md, ...applyAcademicStructure(md, next)}}, options)

  // First visit: persist the defaults-derived levels so they survive navigation and resume.
  // A prefill — the user has not confirmed anything yet.
  useEffect(() => {
    if (isHydrating || isFetchingDetails) return
    const needsSeed = structures.some((entry) => entry.seededFromDetails && entry.levels.length > 0)
    if (needsSeed) commit(structures, {prefill: true})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structures, isHydrating, isFetchingDetails])

  // --- Drawer / dialogs -----------------------------------------------------------------
  const [drawer, setDrawer] = useState<DrawerState | null>(null)
  const [pendingDelete, setPendingDelete] = useState<{curriculum: string; levelId: string} | null>(
    null
  )

  const structureFor = (curriculum: string) =>
    structures.find((entry) => entry.curriculum === curriculum)

  const replaceLevels = (curriculum: string, mapper: (levels: LevelDraft[]) => LevelDraft[]) =>
    commit(
      structures.map((entry) =>
        entry.curriculum === curriculum ? {...entry, levels: mapper(entry.levels)} : entry
      )
    )

  const handleDragEnd = (curriculum: string, result: DropResult) => {
    const {source, destination} = result
    if (!destination || destination.index === source.index) return
    replaceLevels(curriculum, (levels) => {
      const next = [...levels]
      const [moved] = next.splice(source.index, 1)
      next.splice(destination.index, 0, moved)
      return next
    })
  }

  const handleSaveLevel = (level: LevelDraft) => {
    if (!drawer) return
    if (drawer.mode === 'edit') {
      replaceLevels(drawer.curriculum, (levels) =>
        levels.map((entry) => (entry.id === drawer.levelId ? level : entry))
      )
    } else {
      // New levels land at the bottom of the card; the drag handle reorders them.
      replaceLevels(drawer.curriculum, (levels) => [...levels, level])
    }
    setDrawer(null)
  }

  const deleteLevel = (curriculum: string, levelId: string) =>
    replaceLevels(curriculum, (levels) => levels.filter((entry) => entry.id !== levelId))

  // "All levels" means the levels of the curriculum whose drawer is open — other curricula
  // keep their own section settings.
  const applySectionsToAllLevels = (sections: string[]) => {
    if (!drawer) return
    replaceLevels(drawer.curriculum, (levels) =>
      levels.map((level) => ({
        ...level,
        classes: level.classes.map((cls) => ({...cls, sections: [...sections]})),
      }))
    )
  }

  // --- Next ------------------------------------------------------------------------------
  // Level names reused within one curriculum: flagged inline on each row and they keep Next
  // disabled until renamed. Each curriculum is checked on its own.
  const duplicateNamesByCurriculum = new Map(
    structures.map((entry) => [entry.curriculum, duplicateLevelNames(entry)])
  )
  const isComplete = isStructureComplete(structures)
  const nextDisabled = isSubmitting || isFetchingDetails || isHydrating || !isComplete

  const handleNextClick = () => {
    // Re-fold the structure so the flattened classes/subjects reflect the latest branches, then
    // sync classes AND subjects. The old Classes screen sent only 'classes' and left subjects
    // to its separate Subjects screen; with both merged here, subjects otherwise reached the
    // backend only on the Lesson Planning / Students save, so the Lesson Planning template
    // (built server-side from saved data) came back with no subjects. The checkpoint stays
    // 'classes' (see syncIncrementalData).
    const committed = {...md, ...applyAcademicStructure(md, structures)}
    updateData({manualData: committed})
    if (onPartialSubmit) {
      onPartialSubmit(['classes', 'subjects'], committed, () => onNext?.())
    } else {
      onNext?.()
    }
  }

  // --- Row summaries --------------------------------------------------------------------
  const levelIconFor = (index: number) => {
    const Icon = LEVEL_ICONS[index % LEVEL_ICONS.length]
    return <Icon size={20} />
  }

  /** Display-only: level/class names without the curriculum prefix (stored names keep it). */
  const prefixesFor = (entry: CurriculumStructure) =>
    curriculumPrefixes(
      entry.curriculum,
      entry.levels.flatMap((level) => level.classes.map((cls) => cls.name))
    )

  const classRangeLabel = (level: LevelDraft, prefixes: readonly string[]) => {
    const names = level.classes
      .map((cls) => stripCurriculumPrefix(cls.name, prefixes))
      .filter(Boolean)
    if (names.length === 0) return t('NO_CLASSES')
    if (names.length === 1) return names[0]
    return t('CLASS_RANGE', {first: names[0], last: names[names.length - 1]})
  }

  const sectionsSummary = (level: LevelDraft) => {
    const lists = level.classes.map((cls) => cls.sections)
    const total = lists.reduce((sum, list) => sum + list.length, 0)
    if (lists.length === 0 || total === 0) return {line: t('NO_SECTIONS'), total}
    const first = lists[0].join(',')
    const uniform = lists.every((list) => list.join(',') === first)
    return {
      line: uniform
        ? t('SECTIONS_PER_CLASS', {count: lists[0].length, names: lists[0].join(', ')})
        : t('SECTIONS_VARIES'),
      total,
    }
  }

  const textColor = isDark ? '#ffffff' : '#181C32'
  const mutedColor = isDark ? '#92929f' : '#7E8299'
  const cardBorder = isDark ? '#2b2b40' : '#dee2e6'

  const activeDrawerLevel =
    drawer?.mode === 'edit'
      ? structureFor(drawer.curriculum)?.levels.find((level) => level.id === drawer.levelId)
      : undefined
  const activeDrawerIndex =
    drawer?.mode === 'edit'
      ? (structureFor(drawer.curriculum)?.levels.findIndex((l) => l.id === drawer.levelId) ?? -1)
      : -1

  return (
    <div
      className='d-flex flex-column step3-structure'
      // While a level drawer is open the page stays visible and scrollable (the push-mode
      // sheet no longer blocks wheel events) but not clickable: opening another row's
      // Edit/Add from here would replace the drawer and silently drop its unsaved draft.
      // The drawer and dialogs are portaled, so they are unaffected.
      style={{gap: '1.25rem', paddingTop: '1.5rem', pointerEvents: drawer ? 'none' : undefined}}
    >
      <div>
        <h2 style={{color: textColor, fontSize: '1.6rem', fontWeight: 700, marginBottom: '0.4rem'}}>
          {t('HEADER_TITLE')}
        </h2>
        <p style={{color: mutedColor, fontSize: '1rem', lineHeight: 1.6, marginBottom: 0}}>
          {t('HEADER_SUBTITLE')}
        </p>
      </div>

      {structures.map((entry) => (
        <div
          key={entry.curriculum}
          className='step3-structure-card'
          style={{
            border: `1px solid ${cardBorder}`,
            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
          }}
        >
          <div className='step3-structure-card-head' style={{borderBottomColor: cardBorder}}>
            <span className='step3-structure-card-icon'>
              <GraduationCap size={18} />
            </span>
            <span style={{color: textColor, fontWeight: 700, fontSize: '1.02rem'}}>
              {entry.curriculum}
            </span>
          </div>

          <div className='step3-structure-table'>
            <div className='step3-level-row is-header' style={{color: mutedColor}}>
              <span />
              <span>{t('COL_LEVEL')}</span>
              <span>{t('COL_CLASSES')}</span>
              <span>{t('COL_SECTIONS')}</span>
              <span>{t('COL_SUBJECTS')}</span>
              <span className='text-center'>{t('COL_ACTIONS')}</span>
            </div>

            <DragDropContext onDragEnd={(result) => handleDragEnd(entry.curriculum, result)}>
              <Droppable droppableId={`levels-${entry.curriculum}`}>
                {(droppable) => (
                  <div ref={droppable.innerRef} {...droppable.droppableProps}>
                    {entry.levels.map((level, index) => {
                      const sections = sectionsSummary(level)
                      const issues = levelIssues(level)
                      const isDuplicateName =
                        duplicateNamesByCurriculum
                          .get(entry.curriculum)
                          ?.has(level.name.trim().toLowerCase()) ?? false
                      const inlineSubjects = level.subjects.slice(0, MAX_INLINE_SUBJECTS)
                      const hiddenSubjects = level.subjects.length - inlineSubjects.length
                      const isActive =
                        drawer?.mode === 'edit' &&
                        drawer.curriculum === entry.curriculum &&
                        drawer.levelId === level.id
                      return (
                        <Draggable
                          key={level.id}
                          draggableId={`${entry.curriculum}::${level.id}`}
                          index={index}
                        >
                          {(draggable, snapshot) => (
                            <div
                              ref={draggable.innerRef}
                              {...draggable.draggableProps}
                              className={`step3-level-row${isActive ? ' is-active' : ''}${
                                snapshot.isDragging ? ' is-dragging' : ''
                              }${issues.length > 0 || isDuplicateName ? ' has-issues' : ''}`}
                              style={{
                                ...draggable.draggableProps.style,
                                borderTopColor: cardBorder,
                              }}
                            >
                              <span
                                {...draggable.dragHandleProps}
                                className='step3-drag-handle'
                                aria-label={t('DRAG_TO_REORDER')}
                                title={t('DRAG_TO_REORDER')}
                              >
                                <GripVertical size={16} />
                              </span>

                              <div className='step3-level-cell-level'>
                                <span className='step3-level-icon'>{levelIconFor(index)}</span>
                                <div style={{minWidth: 0}}>
                                  <div
                                    style={{color: textColor, fontWeight: 700, fontSize: '0.95rem'}}
                                  >
                                    {stripCurriculumPrefix(level.name, prefixesFor(entry))}
                                  </div>
                                  <div style={{color: mutedColor, fontSize: '0.82rem'}}>
                                    {classRangeLabel(level, prefixesFor(entry))}
                                  </div>
                                  {isDuplicateName && (
                                    <div
                                      role='alert'
                                      style={{
                                        color: '#f1416c',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        marginTop: 2,
                                      }}
                                    >
                                      {t('LEVEL_NAME_DUPLICATE')}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div style={{color: textColor, fontSize: '0.9rem'}}>
                                {t('CLASSES_COUNT', {count: level.classes.length})}
                              </div>

                              <div style={{fontSize: '0.88rem', color: textColor}}>
                                <div>{sections.line}</div>
                                <div style={{color: mutedColor, fontSize: '0.82rem'}}>
                                  {t('SECTIONS_TOTAL', {count: sections.total})}
                                </div>
                              </div>

                              <div className='step3-level-cell-subjects'>
                                {level.subjects.length > MAX_INLINE_SUBJECTS && (
                                  <div style={{color: mutedColor, fontSize: '0.78rem'}}>
                                    {t('SUBJECTS_SELECTED', {count: level.subjects.length})}
                                  </div>
                                )}
                                {level.subjects.length === 0 ? (
                                  <span
                                    style={{color: '#f1416c', fontSize: '0.82rem', fontWeight: 600}}
                                  >
                                    {t('NO_SUBJECTS')}
                                  </span>
                                ) : (
                                  <div className='d-flex flex-column gap-1'>
                                    {chunk(
                                      [
                                        ...inlineSubjects.map((subject) => (
                                          <span key={subject} className='step3-subject-tag'>
                                            {subject}
                                          </span>
                                        )),
                                        ...(hiddenSubjects > 0
                                          ? [
                                              <span
                                                key='__more'
                                                className='step3-subject-tag is-more'
                                              >
                                                {t('SUBJECTS_MORE', {count: hiddenSubjects})}
                                              </span>,
                                            ]
                                          : []),
                                      ],
                                      SUBJECTS_PER_LINE
                                    ).map((line, lineIdx) => (
                                      <div key={lineIdx} className='d-flex flex-wrap gap-1'>
                                        {line}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <div className='step3-level-actions'>
                                <button
                                  type='button'
                                  className='step3-level-edit-btn'
                                  aria-label={intl.formatMessage({id: 'GENERAL.EDIT'})}
                                  title={intl.formatMessage({id: 'GENERAL.EDIT'})}
                                  onClick={() =>
                                    setDrawer({
                                      mode: 'edit',
                                      curriculum: entry.curriculum,
                                      levelId: level.id,
                                    })
                                  }
                                >
                                  <Pencil size={15} />
                                </button>
                                <button
                                  type='button'
                                  className='step3-level-delete-btn'
                                  aria-label={intl.formatMessage({id: 'GENERAL.DELETE'})}
                                  title={intl.formatMessage({id: 'GENERAL.DELETE'})}
                                  onClick={() =>
                                    setPendingDelete({
                                      curriculum: entry.curriculum,
                                      levelId: level.id,
                                    })
                                  }
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      )
                    })}
                    {droppable.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {entry.levels.length === 0 && (
              <div
                className='step3-level-row is-empty'
                style={{color: mutedColor, borderTopColor: cardBorder}}
              >
                {t('NO_LEVELS')}
              </div>
            )}

            <button
              type='button'
              className='step3-add-level-btn'
              onClick={() => setDrawer({mode: 'add', curriculum: entry.curriculum})}
            >
              <Plus size={15} strokeWidth={2.5} />
              {t('ADD_LEVEL')}
            </button>
          </div>
        </div>
      ))}

      <div
        className='step3-structure-note'
        style={{
          border: `1px solid ${cardBorder}`,
          backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f9f9fb',
          color: isComplete ? mutedColor : '#f1416c',
        }}
      >
        <Info size={16} style={{flexShrink: 0}} />
        <span>{isComplete ? t('REVIEW_NOTE') : t('VALIDATION')}</span>
      </div>

      <div className='d-flex justify-content-between mt-2'>
        <button
          type='button'
          className='onboarding-btn-back'
          disabled={isSubmitting}
          onClick={onBack}
        >
          <ArrowLeft size={16} className='me-2' />
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
        </button>
        <button
          type='button'
          className='onboarding-btn-next'
          disabled={nextDisabled}
          onClick={handleNextClick}
        >
          {isSubmitting ? (
            <>
              <span className='spinner-border spinner-border-sm align-middle me-2' />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SAVING'})}
            </>
          ) : (
            intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.NEXT'})
          )}
          <ArrowRight size={16} className='ms-2' />
        </button>
      </div>

      {drawer && (drawer.mode === 'add' || activeDrawerLevel) && (
        <Step3LevelDrawer
          key={drawer.mode === 'edit' ? `edit-${drawer.levelId}` : `add-${drawer.curriculum}`}
          mode={drawer.mode}
          curriculum={drawer.curriculum}
          level={drawer.mode === 'edit' ? (activeDrawerLevel ?? null) : null}
          levelIcon={activeDrawerIndex >= 0 ? levelIconFor(activeDrawerIndex) : undefined}
          // Uniqueness is per curriculum: only this curriculum's other levels (the level being
          // edited left out) — their names, and the class names they already use.
          siblingNames={(structureFor(drawer.curriculum)?.levels ?? [])
            .filter((level) => drawer.mode === 'add' || level.id !== drawer.levelId)
            .map((level) => level.name)}
          siblingClassNames={(structureFor(drawer.curriculum)?.levels ?? [])
            .filter((level) => drawer.mode === 'add' || level.id !== drawer.levelId)
            .flatMap((level) => level.classes.map((cls) => cls.name))}
          classNamePrefix={
            // Plain names: the curriculum is stored on the level, never written into a name.
            usesPlainCurriculumNames()
              ? ''
              : classNamePrefixFor(drawer.curriculum, structureFor(drawer.curriculum))
          }
          subjectPool={subjectPoolForCurriculum(
            drawer.curriculum,
            structureFor(drawer.curriculum),
            details
          )}
          defaultSections={defaultSections}
          isDark={isDark}
          onSave={handleSaveLevel}
          onDelete={
            drawer.mode === 'edit'
              ? () => {
                  deleteLevel(drawer.curriculum, drawer.levelId)
                  setDrawer(null)
                }
              : undefined
          }
          onApplySectionsToAllLevels={drawer.mode === 'edit' ? applySectionsToAllLevels : undefined}
          onClose={() => setDrawer(null)}
        />
      )}

      <WizardConfirmDialog
        open={pendingDelete !== null}
        tone='danger'
        isDark={isDark}
        title={intl.formatMessage({id: 'MENU.ARE_YOU_SURE?'})}
        body={t('DELETE_LEVEL_CONFIRM')}
        confirmLabel={intl.formatMessage({id: 'GENERAL.DELETE'})}
        cancelLabel={intl.formatMessage({id: 'GENERAL.CANCEL'})}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) deleteLevel(pendingDelete.curriculum, pendingDelete.levelId)
          setPendingDelete(null)
        }}
      />
    </div>
  )
}

export default Step3AcademicStructure
